import React, { useState, useEffect } from 'react';
import { Layout } from 'antd';
import { useParams } from 'react-router-dom';
import {
  FaEye,
  FaLayerGroup,
  FaTasks,
  FaDollarSign,
} from 'react-icons/fa';

import VisaoGeral from '../pages/Projetos/VisaoGeral';
import Etapas from '../pages/Projetos/Etapas';
import Tarefas from '../pages/Projetos/Tarefas';
import Custos from '../pages/Projetos/Custos';

const { Content } = Layout;

/* Estilos de animação injetados uma única vez */
const animationStyles = `
  @keyframes fadeSlideIn {
    0% {
      opacity: 0;
      transform: translateY(12px);
    }
    100% {
      opacity: 1;
      transform: translateY(0);
    }
  }
  @keyframes indicatorSlide {
    0% { transform: translateX(-50%) scaleX(0.8); }
    100% { transform: translateX(-50%) scaleX(1); }
  }
  .page-transition-enter {
    animation: fadeSlideIn 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  }
`;

const ProjetoDetalhes = () => {
  const { id } = useParams();
  const [activeMenu, setActiveMenu] = useState('visao-geral');
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    if (!document.getElementById('projeto-detalhes-animations')) {
      const styleTag = document.createElement('style');
      styleTag.id = 'projeto-detalhes-animations';
      styleTag.textContent = animationStyles;
      document.head.appendChild(styleTag);
    }
  }, []);

  const handleMenuClick = (key) => {
    setAnimate(false);
    setTimeout(() => {
      setActiveMenu(key);
      setAnimate(true);
    }, 10);
  };

  const menus = [
    { key: 'visao-geral', label: 'Visão Geral', icon: <FaEye /> },
    { key: 'etapas', label: 'Etapas', icon: <FaLayerGroup /> },
    { key: 'tarefas', label: 'Tarefas', icon: <FaTasks /> },
    { key: 'custos', label: 'Custos', icon: <FaDollarSign /> },
  ];

  const renderContent = () => {
    const componentMap = {
      'visao-geral': <VisaoGeral projetoId={id} />,
      etapas: <Etapas projetoId={id} />,
      tarefas: <Tarefas projetoId={id} />,
      custos: <Custos projetoId={id} />,
    };
    return (
      <div className={animate ? 'page-transition-enter' : ''}>
        {componentMap[activeMenu]}
      </div>
    );
  };

  return (
    <Layout
      style={{
        background: 'linear-gradient(135deg, #f0f5ff 0%, #f5f7fa 100%)',
        minHeight: '100vh',
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
      }}
    >
      {/* Conteúdo sem restrições de largura, apenas espaçamento vertical */}
      <Content style={{ padding: '32px 0' }}>
        {/* Menu horizontal centralizado e adaptável */}
        <nav
          style={{
            background: 'rgba(255,255,255,0.7)',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            borderRadius: 60,
            padding: '6px 8px',
            margin: '0 auto 40px',
            maxWidth: 900, // largura controlada apenas para o menu, não para todo o conteúdo
            boxShadow: '0 10px 30px rgba(0,0,0,0.05), 0 4px 12px rgba(22,119,255,0.08)',
            border: '1px solid rgba(255,255,255,0.5)',
            display: 'flex',
            justifyContent: 'center',
            flexWrap: 'wrap',
            gap: 8,
          }}
        >
          {menus.map((menu) => {
            const isActive = activeMenu === menu.key;
            return (
              <button
                key={menu.key}
                onClick={() => handleMenuClick(menu.key)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '14px 32px',
                  borderRadius: 50,
                  border: 'none',
                  background: isActive
                    ? 'linear-gradient(135deg, #1677ff 0%, #4096ff 100%)'
                    : 'transparent',
                  color: isActive ? '#fff' : '#1f1f1f',
                  fontWeight: isActive ? 600 : 500,
                  fontSize: 16,
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                  boxShadow: isActive
                    ? '0 8px 20px rgba(22,119,255,0.35), 0 2px 6px rgba(22,119,255,0.2)'
                    : 'none',
                  transform: isActive ? 'scale(1.02)' : 'scale(1)',
                  letterSpacing: '0.01em',
                  position: 'relative',
                  overflow: 'hidden',
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.background = 'rgba(22,119,255,0.06)';
                    e.currentTarget.style.color = '#1677ff';
                    e.currentTarget.style.transform = 'scale(1.02)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = '#1f1f1f';
                    e.currentTarget.style.transform = 'scale(1)';
                  }
                }}
              >
                <span style={{ fontSize: 20, display: 'flex', alignItems: 'center' }}>
                  {menu.icon}
                </span>
                {menu.label}
                {isActive && (
                  <span
                    style={{
                      position: 'absolute',
                      bottom: 0,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: '30%',
                      height: 3,
                      borderRadius: 3,
                      background: 'rgba(255,255,255,0.8)',
                      animation: 'indicatorSlide 0.3s ease-out',
                    }}
                  />
                )}
              </button>
            );
          })}
        </nav>

        {/* Bloco de conteúdo ocupando 100% da largura disponível */}
        <div
          style={{
            width: '100%',
            background: '#ffffff',
            borderRadius: 0, // sem arredondamento para ocupar toda a largura
            padding: '32px 36px',
            minHeight: 500,
            boxShadow: '0 8px 24px rgba(0,0,0,0.04), 0 2px 8px rgba(22,119,255,0.06)',
            border: '1px solid rgba(22,119,255,0.1)',
            boxSizing: 'border-box',
          }}
        >
          {renderContent()}
        </div>
      </Content>
    </Layout>
  );
};

export default ProjetoDetalhes;